// core/modals/peoples/AddCustomer.jsx
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { X, Save, User, Phone, Mail, MapPin } from 'react-feather';
import Swal from 'sweetalert2';
import { customerActions } from '../../action/customerActions';

const AddCustomer = ({ isOpen, onClose, onSuccess }) => {
    const modalRef = useRef(null);
    
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        address: '',
        gst_no: ''
    });
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Clean up modal backdrops
    const cleanupModalBackdrops = () => {
        const backdrops = document.querySelectorAll('.modal-backdrop');
        backdrops.forEach(backdrop => backdrop.remove());
        document.body.classList.remove('modal-open');
        document.body.style.paddingRight = '';
        document.body.style.overflow = '';
    };

    // Show Bootstrap modal
    const showBootstrapModal = () => {
        cleanupModalBackdrops();
        const modalElement = document.getElementById('add-customer');
        if (modalElement) {
            if (typeof window.$ !== 'undefined' && window.$.fn.modal) {
                window.$(modalElement).modal('show');
            } else if (typeof window.bootstrap !== 'undefined' && window.bootstrap.Modal) {
                let modalInstance = window.bootstrap.Modal.getInstance(modalElement);
                if (!modalInstance) {
                    modalInstance = new window.bootstrap.Modal(modalElement);
                }
                modalInstance.show();
            } else {
                modalElement.classList.add('show');
                modalElement.style.display = 'block';
                const backdrop = document.createElement('div');
                backdrop.className = 'modal-backdrop fade show';
                document.body.appendChild(backdrop);
                document.body.classList.add('modal-open');
            }
        }
    };

    // Hide Bootstrap modal
    const hideBootstrapModal = () => {
        const modalElement = document.getElementById('add-customer');
        if (modalElement) {
            if (typeof window.$ !== 'undefined' && window.$.fn.modal) {
                window.$(modalElement).modal('hide');
                setTimeout(cleanupModalBackdrops, 300);
            } else if (typeof window.bootstrap !== 'undefined' && window.bootstrap.Modal) {
                const modalInstance = window.bootstrap.Modal.getInstance(modalElement);
                if (modalInstance) {
                    modalInstance.hide();
                    setTimeout(cleanupModalBackdrops, 300);
                }
            } else {
                modalElement.classList.remove('show');
                modalElement.style.display = 'none';
                cleanupModalBackdrops();
            }
        }
    };

    useEffect(() => {
        if (isOpen) {
            setError('');
            setTimeout(() => showBootstrapModal(), 50);
        }
        return () => {
            cleanupModalBackdrops();
        };
    }, [isOpen]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (error) setError('');
    };

    const validateForm = () => {
        if (!formData.name.trim()) {
            setError('Customer name is required');
            Swal.fire('Error', 'Customer name is required', 'error');
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) return;
        
        try {
            setLoading(true);
            setError('');
            
            console.log('Submitting customer data:', formData);
            
            const result = await customerActions.create(formData);
            
            console.log('Customer creation result:', result);
            
            if (result && result.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: 'Customer added successfully',
                    timer: 1500,
                    showConfirmButton: false
                });
                
                // Pass the customer data back to parent
                if (onSuccess && result.data) {
                    onSuccess(result.data);
                } else if (onSuccess && result.customer) {
                    onSuccess(result.customer);
                } else if (onSuccess && result.data?.customer) {
                    onSuccess(result.data.customer);
                }
                
                // Reset form
                setFormData({
                    name: '',
                    email: '',
                    phone: '',
                    address: '',
                    gst_no: ''
                });
                
                // Close modal
                hideBootstrapModal();
                setTimeout(() => {
                    if (onClose) onClose();
                }, 300);
            } else {
                const errorMsg = result?.message || 'Failed to add customer';
                setError(errorMsg);
                Swal.fire('Error', errorMsg, 'error');
            }
        } catch (error) {
            console.error('Error adding customer:', error);
            const errorMsg = error.response?.data?.message || error.message || 'Failed to add customer';
            setError(errorMsg);
            Swal.fire('Error', errorMsg, 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        if (formData.name || formData.phone || formData.email) {
            Swal.fire({
                title: 'Discard Changes?',
                text: 'You have unsaved changes. Are you sure?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                confirmButtonText: 'Yes, discard'
            }).then((result) => {
                if (result.isConfirmed) {
                    setFormData({
                        name: '',
                        email: '',
                        phone: '',
                        address: '',
                        gst_no: ''
                    });
                    setError('');
                    hideBootstrapModal();
                    setTimeout(() => {
                        if (onClose) onClose();
                    }, 300);
                }
            });
        } else {
            hideBootstrapModal();
            setTimeout(() => {
                if (onClose) onClose();
            }, 300);
        }
    };

    return (
        <div className="modal fade" id="add-customer" ref={modalRef} tabIndex="-1" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Add New Customer</h5>
                        <button type="button" className="btn-close" onClick={handleCancel}></button>
                    </div>
                    
                    <div className="modal-body">
                        {error && (
                            <div className="alert alert-danger alert-dismissible fade show" role="alert">
                                {error}
                                <button type="button" className="btn-close" onClick={() => setError('')}></button>
                            </div>
                        )}
                        
                        <form onSubmit={handleSubmit}>
                            <div className="mb-3">
                                <label className="form-label fw-bold">
                                    <User size={14} className="me-1" />
                                    Customer Name <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    placeholder="Enter customer name"
                                    required
                                />
                            </div>
                            
                            <div className="mb-3">
                                <label className="form-label fw-bold">
                                    <Phone size={14} className="me-1" />
                                    Phone Number
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    placeholder="Enter phone number"
                                />
                            </div>
                            
                            <div className="mb-3">
                                <label className="form-label fw-bold">
                                    <Mail size={14} className="me-1" />
                                    Email Address
                                </label>
                                <input
                                    type="email"
                                    className="form-control"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    placeholder="Enter email address"
                                />
                            </div>
                            
                            <div className="mb-3">
                                <label className="form-label fw-bold">
                                    <MapPin size={14} className="me-1" />
                                    Address
                                </label>
                                <textarea
                                    className="form-control"
                                    name="address"
                                    value={formData.address}
                                    onChange={handleChange}
                                    placeholder="Enter address"
                                    rows="2"
                                />
                            </div>
                            
                            <div className="mb-3">
                                <label className="form-label fw-bold">GST Number</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    name="gst_no"
                                    value={formData.gst_no}
                                    onChange={handleChange}
                                    placeholder="Enter GST number"
                                />
                            </div>
                            
                            <div className="text-end">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary me-2"
                                    onClick={handleCancel}
                                >
                                    <X size={14} className="me-1" />
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-1" />
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <Save size={14} className="me-1" />
                                            Save Customer
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

AddCustomer.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSuccess: PropTypes.func
};

export default AddCustomer;